import { Injectable } from "@angular/core";
import {
    Observable, of
} from "rxjs";
import { Container } from "@shopliftr/common-js/shared";
import {
    EnumUtil,
    StringUtil
} from "@shopliftr/common-js/data";
import { IAutocompleteController } from "../../interfaces/autocomplete-controller";
import { IAutocompleteResults } from "../../interfaces/autocomplete-results";
import { IAutocompleteOption } from "../../interfaces/autocomplete-option";

/**
 * Autocomplete controller service for Container enum values.
 * Uses Container enum values as both the value and display name.
 */
@Injectable({
    providedIn: "root"
})
export class ContainerAutocompleteControllerService implements IAutocompleteController<Container, any> {

    /**
     * Execute an autocomplete request for containers
     *
     * @param query - The text query used to filter the autocomplete options
     * @param options - Additional options (not used for container enum)
     * @returns Observable of autocomplete results
     */
    autocomplete$(query?: string, options?: any): Observable<IAutocompleteResults<Container>> {
        // Get all Container enum values
        const containerValues = EnumUtil.getValues(Container) as Array<Container>;

        // Filter containers based on query if provided
        let filteredContainers = containerValues;
        const sanitizedQuery = StringUtil.sanitizeString(query);
        if (sanitizedQuery) {
            const queryLower = sanitizedQuery.toLowerCase();
            filteredContainers = containerValues.filter((container) =>
                container.toLowerCase().includes(queryLower));
        }

        // Convert to autocomplete options
        const autocompleteOptions: Array<IAutocompleteOption<Container>> = filteredContainers.map((container) => ({
            option: container,
            value: container
        }));

        const results: IAutocompleteResults<Container> = {
            options: autocompleteOptions
        };

        return of(results);
    }

    /**
     * Returns the string to be displayed in the autocomplete list and textbox when selected
     *
     * @param value - The container enum value
     * @param options - Additional options (not used for containers)
     * @returns The display string
     */
    getAutocompleteDisplayName(value?: Container, options?: any): string {
        return value || "";
    }

    /**
     * Used as the identity track expression for the MatOption list
     *
     * @param index - Index in the for loop
     * @param result - Item at the index
     * @returns Unique identifier
     */
    trackByIdentifier(index: number, result: IAutocompleteOption<Container>): any {
        return result?.value;
    }

    /**
     * Determines if the container is unique compared to the containers in the array
     *
     * @param value - The container to check
     * @param items - Array of existing containers
     * @returns True if the container is unique
     */
    isUnique(value: Container, items: Array<Container>): boolean {
        return !items?.includes(value);
    }
}
